import React, { useEffect, useRef, useState } from 'react';
import mermaid from 'mermaid';
import { toPng } from 'html-to-image';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Dialog,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';

// ─── Mermaid global config ────────────────────────────────────────────────────

mermaid.initialize({
  startOnLoad: false,
  theme: 'base',
  securityLevel: 'loose',
  suppressErrorRendering: true,
  fontSize: 14,
  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  themeVariables: {
    // Base colors
    primaryColor: '#E6F1FB',
    primaryTextColor: '#0C447C',
    primaryBorderColor: '#185FA5',
    lineColor: '#888780',
    secondaryColor: '#EEEDFE',
    tertiaryColor: '#E1F5EE',
    // Node fills per type
    background: '#ffffff',
    mainBkg: '#E6F1FB',
    nodeBorder: '#185FA5',
    clusterBkg: '#f8f9fa',
    titleColor: '#2C2C2A',
    edgeLabelBackground: '#ffffff',
    // Flowchart specific
    nodeTextColor: '#0C447C',
    // Class diagram
    classText: '#0C447C',
    // Sequence diagram
    actorBkg: '#E6F1FB',
    actorBorder: '#185FA5',
    actorTextColor: '#0C447C',
    actorLineColor: '#888780',
    signalColor: '#534AB7',
    signalTextColor: '#26215C',
    labelBoxBkgColor: '#EEEDFE',
    labelBoxBorderColor: '#534AB7',
    labelTextColor: '#26215C',
    loopTextColor: '#26215C',
    noteBorderColor: '#854F0B',
    noteBkgColor: '#FAEEDA',
    noteTextColor: '#412402',
    activationBorderColor: '#185FA5',
    activationBkgColor: '#B5D4F4',
    // Gantt
    gridColor: '#D3D1C7',
    doneTaskBkgColor: '#9FE1CB',
    doneTaskBorderColor: '#0F6E56',
    activeTaskBkgColor: '#B5D4F4',
    activeTaskBorderColor: '#185FA5',
    taskTextColor: '#2C2C2A',
    taskTextOutsideColor: '#2C2C2A',
    critBkgColor: '#F5C4B3',
    critBorderColor: '#993C1D',
    // Pie chart
    pie1: '#378ADD',
    pie2: '#534AB7',
    pie3: '#1D9E75',
    pie4: '#D85A30',
    pie5: '#BA7517',
    pie6: '#639922',
    pie7: '#D4537E',
    pie8: '#888780',
    pieSectionTextColor: '#ffffff',
    pieStrokeColor: '#ffffff',
    pieStrokeWidth: '2px',
    pieOpacity: '0.9',
    // Mindmap
    mm1: '#E6F1FB',
    mm2: '#EEEDFE',
    mm3: '#E1F5EE',
    mm4: '#FAECE7',
    mm5: '#FAEEDA',
    mm6: '#EAF3DE',
    mm7: '#FBEAF0',
  },
  flowchart: {
    useMaxWidth: true,
    htmlLabels: true,
    nodeSpacing: 40,
    rankSpacing: 50,
    curve: 'basis',
    padding: 12,
  },
  sequence: {
    diagramMarginX: 20,
    diagramMarginY: 20,
    actorMargin: 50,
    width: 150,
    height: 65,
    boxMargin: 10,
    boxTextMargin: 5,
    noteMargin: 10,
    messageMargin: 35,
    mirrorActors: true,
    bottomMarginAdj: 1,
    useMaxWidth: true,
  },
  mindmap: {
    useMaxWidth: true,
    padding: 16,
  },
});

// ─── Types ────────────────────────────────────────────────────────────────────

interface Props {
  code: string;
  title?: string;
}

// ─── Sanitize mermaid code ────────────────────────────────────────────────────

const sanitizeMermaidCode = (raw: string): string => {
  let code = raw.replace(/\\n/g, '\n').trim();

  const lines = code.split('\n').map((line) => {
    let l = line.trimEnd();

    l = l.replace(/\[([^\]]+)\]/g, (_m, inner) => {
      const cleaned = inner.replace(/[()]/g, '').replace(/"/g, "'").trim();
      return `["${cleaned}"]`;
    });

    l = l.replace(
      /([A-Za-z0-9_]+)\s+--\s+([^->\n|"]+?)\s+-->\s+([A-Za-z0-9_]+)/g,
      (_m, from, label, to) => {
        const cleanLabel = label.replace(/[()]/g, '').replace(/"/g, "'").trim();
        return `${from} -->|"${cleanLabel}"| ${to}`;
      }
    );

    l = l.replace(
      /([A-Za-z0-9_]+)\s+--([^>\n|"{}[\]]+?)-->\s+([A-Za-z0-9_]+)/g,
      (_m, from, label, to) => {
        const cleanLabel = label.replace(/[()]/g, '').replace(/"/g, "'").trim();
        if (!cleanLabel) return `${from} --> ${to}`;
        return `${from} -->|"${cleanLabel}"| ${to}`;
      }
    );

    return l;
  });

  return lines.join('\n');
};

// ─── Inject font + color overrides into rendered SVG ─────────────────────────

const injectStyles = (svgString: string): string => {
  const styleTag = `<style>
    * { box-sizing: border-box; }

    /* Base font */
    .label, .nodeLabel, .edgeLabel, text, tspan,
    .node text, .cluster-label text,
    .actor, .messageText, .labelText, .loopText,
    .noteText, .titleText, .pieTitleText,
    foreignObject div, .mindmap-node .label {
      font-size: 13px !important;
      font-family: "Roboto", "Helvetica", "Arial", sans-serif !important;
    }

    /* Flowchart nodes */
    .node rect, .node circle, .node ellipse,
    .node polygon, .node path {
      stroke-width: 1.5px !important;
    }

    /* Mindmap nodes — ensure text fits */
    .mindmap-node circle, .mindmap-node rect,
    .mindmap-node ellipse, .mindmap-node polygon {
      stroke-width: 1.5px !important;
    }
    .mindmap-node .label {
      font-size: 12px !important;
      font-weight: 500 !important;
      overflow: visible !important;
      white-space: normal !important;
      word-break: break-word !important;
    }

    /* Sequence diagram */
    .actor-line { stroke: #888780 !important; }

    /* Pie chart labels */
    .pieTitleText { font-size: 15px !important; font-weight: 600 !important; }
    .slice { stroke-width: 2px !important; }

    /* Gantt */
    .grid .tick line { stroke: #D3D1C7 !important; }
    .grid path { stroke-width: 0 !important; }

    /* Edge labels background */
    .edgeLabel .label { background: #ffffff !important; border-radius: 4px !important; padding: 2px 4px !important; }
  </style>`;
  return svgString.replace(/(<svg[^>]*>)/, `$1${styleTag}`);
};

// ─── Detect diagram type from code ───────────────────────────────────────────

const detectDiagramType = (code: string): string => {
  const first = code.trim().split('\n')[0].toLowerCase();
  if (first.startsWith('mindmap'))    return 'mindmap';
  if (first.startsWith('sequencediagram') || first.startsWith('sequence')) return 'sequence';
  if (first.startsWith('gantt'))      return 'gantt';
  if (first.startsWith('pie'))        return 'pie';
  if (first.startsWith('classd'))     return 'class';
  if (first.startsWith('erdiagram'))  return 'er';
  if (first.startsWith('statediagram')) return 'state';
  if (first.startsWith('gitgraph') || first.startsWith('gitflow')) return 'git';
  return 'flowchart';
};

const DIAGRAM_ICONS: Record<string, string> = {
  mindmap:  '🧠',
  sequence: '🔁',
  gantt:    '📅',
  pie:      '🥧',
  class:    '📦',
  er:       '🗃️',
  state:    '🔄',
  git:      '🌿',
  flowchart:'📊',
};

// ─── Render hook ──────────────────────────────────────────────────────────────

const useMermaidRender = (code: string) => {
  const [svg, setSvg] = useState<string>('');
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!code) return;
    const cleanCode = sanitizeMermaidCode(code);
    const id = `mermaid-${Date.now()}-${Math.floor(Math.random() * 10000)}`;

    setSvg('');
    setError(false);

    mermaid
      .render(id, cleanCode)
      .then(({ svg: renderedSvg }) => {
        setSvg(injectStyles(renderedSvg));
      })
      .catch((err: Error) => {
        console.warn('Mermaid render failed:', err.message);
        setError(true);
      });
  }, [code]);

  return { svg, error };
};

// ─── PNG download ─────────────────────────────────────────────────────────────

const downloadDiagramAsPng = (
  nodeRef: React.RefObject<HTMLDivElement>,
  title?: string
) => {
  const node = nodeRef.current;
  if (!node) return;
  toPng(node, { backgroundColor: '#ffffff', pixelRatio: 2 })
    .then((dataUrl) => {
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = `${title || 'diagram'}.png`;
      a.click();
    })
    .catch((err) => console.error('html-to-image failed:', err));
};

// ─── Full-screen dialog ───────────────────────────────────────────────────────

interface FullDiagramDialogProps {
  open: boolean;
  onClose: () => void;
  svg: string;
  title?: string;
}

const FullDiagramDialog: React.FC<FullDiagramDialogProps> = ({
  open,
  onClose,
  svg,
  title,
}) => {
  const downloadRef = useRef<HTMLDivElement>(null);
  const diagramType = title ? '' : 'diagram';
  const icon = DIAGRAM_ICONS[detectDiagramType('')] ?? '📊';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, maxHeight: '90vh', m: 2 },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 1.5,
          px: 2,
          borderBottom: '1px solid #e5e7eb',
          fontSize: 14,
          fontWeight: 600,
          color: '#555',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <span>📊</span>
          <span>{title || 'Diagram'}</span>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Download as PNG" arrow>
            <IconButton
              size="small"
              onClick={() => downloadDiagramAsPng(downloadRef, title)}
              sx={{
                color: '#667eea',
                '&:hover': { bgcolor: 'rgba(102,126,234,0.08)' },
              }}
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <IconButton size="small" onClick={onClose} sx={{ color: '#999' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3, overflowX: 'auto', bgcolor: '#fafafa' }}>
        <Box
          ref={downloadRef}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            bgcolor: '#ffffff',
            p: 3,
            borderRadius: 2,
            // Full-size: SVG expands freely
            '& svg': {
              maxWidth: '100%',
              height: 'auto',
              width: 'auto',
            },
            '& .label, & .nodeLabel, & .edgeLabel, & text, & tspan,& foreignObject div': {
              fontSize: '13px !important',
              fontFamily: '"Roboto","Helvetica","Arial",sans-serif !important',
            },
          }}
          dangerouslySetInnerHTML={{ __html: svg }}
        />
      </DialogContent>
    </Dialog>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const MermaidDiagram: React.FC<Props> = ({ code, title }) => {
  const { svg, error } = useMermaidRender(code);
  const [dialogOpen, setDialogOpen] = useState(false);
  const previewRef = useRef<HTMLDivElement>(null);

  const diagramType = detectDiagramType(code);
  const icon = DIAGRAM_ICONS[diagramType] ?? '📊';

  if (error || !code) return null;

  if (!svg) {
    return (
      <Box
        sx={{
          my: 1,
          p: 1.5,
          bgcolor: '#f8f9fa',
          borderRadius: 2,
          border: '1px solid #e0e0e0',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Typography sx={{ fontSize: 11, color: '#999' }}>
          {icon} Rendering diagram…
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          my: 1,
          bgcolor: '#f8f9fa',
          borderRadius: 2,
          border: '1px solid #e0e0e0',
          overflow: 'hidden',
          position: 'relative',
          cursor: 'pointer',
          transition: 'box-shadow 0.2s',
          '&:hover': {
            boxShadow: '0 2px 12px rgba(102,126,234,0.18)',
            borderColor: '#667eea',
          },
          '&:hover .diagram-overlay': { opacity: 1 },
        }}
        onClick={() => setDialogOpen(true)}
      >
        {/* ── Header bar ── */}
        <Box
          sx={{
            px: 1.5,
            py: 0.75,
            borderBottom: '1px solid #e0e0e0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: '#fff',
          }}
        >
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 600,
              color: '#555',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <span>{icon}</span>
            <span>{title || 'Diagram'}</span>
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            <Tooltip title="Download as PNG" arrow>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  downloadDiagramAsPng(previewRef, title);
                }}
                sx={{
                  p: 0.4,
                  color: '#999',
                  '&:hover': { color: '#667eea', bgcolor: 'rgba(102,126,234,0.08)' },
                }}
              >
                <DownloadIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="View full size" arrow>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setDialogOpen(true);
                }}
                sx={{
                  p: 0.4,
                  color: '#999',
                  '&:hover': { color: '#667eea', bgcolor: 'rgba(102,126,234,0.08)' },
                }}
              >
                <ZoomInIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* ── Compact preview ── */}
        {/* maxHeight keeps the card small; SVG is scaled down via CSS */}
        <Box
          ref={previewRef}
          sx={{
            p: 1.5,
            overflowX: 'auto',
            overflowY: 'hidden',
            textAlign: 'center',
            position: 'relative',
            bgcolor: '#f8f9fa',
            // ↓ KEY FIX: compact height in preview
            maxHeight: 220,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            '& svg': {
              // Scale SVG down to fit the preview box while keeping aspect ratio
              maxWidth: '100%',
              maxHeight: '200px',
              width: 'auto',
              height: 'auto',
              display: 'block',
            },
            '& .label, & .nodeLabel, & .edgeLabel, & text, & tspan, & .node text,& foreignObject div': {
              fontSize: '13px !important',
              fontFamily: '"Roboto","Helvetica","Arial",sans-serif !important',
            },
          }}
          dangerouslySetInnerHTML={{ __html: svg }}
        />

        {/* ── Hover overlay ── */}
        <Box
          className="diagram-overlay"
          sx={{
            position: 'absolute',
            inset: 0,
            bgcolor: 'rgba(102,126,234,0.06)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: 0,
            transition: 'opacity 0.2s',
            pointerEvents: 'none',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              bgcolor: 'rgba(255,255,255,0.92)',
              borderRadius: 2,
              px: 1.5,
              py: 0.75,
              border: '1px solid #667eea',
            }}
          >
            <ZoomInIcon sx={{ fontSize: 14, color: '#667eea' }} />
            <Typography sx={{ fontSize: 11, color: '#667eea', fontWeight: 600 }}>
              Click to expand
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* ── Full-screen dialog ── */}
      <FullDiagramDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        svg={svg}
        title={title}
      />
    </>
  );
};

export default MermaidDiagram;